import { Box, Container, Typography, Grid, Chip } from '@mui/material';
import { Phone, PhoneOutgoing, MessageSquare, Check } from 'lucide-react';
import Reveal from './Reveal';
import { tokens } from '../theme';

const blocks = [
  {
    icon: Phone,
    accent: 'sage' as const,
    tag: 'AI Receptionist',
    title: 'Never misses a call.',
    body:
      'A real conversation with your customer — they call, your AI answers in their language, takes the order, books the table, transfers when it actually needs a human, and texts a follow-up. 24/7, every weekend, every holiday. Available on Professional and above.',
    bullets: [
      'Bookings, orders, FAQ — handled live',
      'Transfers to a human when it should',
      'End-of-call SMS with the order summary',
    ],
  },
  {
    icon: PhoneOutgoing,
    accent: 'copper' as const,
    tag: 'Outbound AI Calls',
    title: 'Makes the calls you dread.',
    body:
      'Ask CleoleadAI to call your suppliers, confirm tomorrow\'s reservations, or chase a late delivery — it proposes the calls, you approve, it dials. Batch a whole list and read the transcripts when it\'s done.',
    bullets: [
      'You approve every call before it dials',
      'Batch dialing: one ask, many calls',
      'Full transcripts + outcomes in your inbox',
    ],
  },
  {
    icon: MessageSquare,
    accent: 'copper' as const,
    tag: 'Two-way SMS',
    title: 'SMS that closes loops.',
    body:
      'Order confirmations, appointment reminders, support replies, re-engagement campaigns — your AI handles inbound and outbound text. Same brain as chat and voice, no separate setup, no surprise bills.',
    bullets: [
      'Send + receive on your verified numbers',
      'Same dashboard as IG / FB / WhatsApp',
      'Metered usage, predictable plans',
    ],
  },
];

export default function VoiceSmsSection() {
  return (
    <Box
      id="voice-sms"
      sx={{
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Reveal sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
            Voice + SMS
          </Typography>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Phone. Text. Same AI brain.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 620, mx: 'auto' }}
          >
            Your AI receptionist answers calls and texts on dedicated business numbers — joining
            Instagram, Facebook, Telegram, WhatsApp, and the web widget, all trained on your business.
          </Typography>
        </Reveal>

        <Grid container spacing={3}>
          {blocks.map((b, index) => {
            const Icon = b.icon;
            const isSage = b.accent === 'sage';
            return (
              <Grid size={{ xs: 12, md: 4 }} key={b.title}>
                <Reveal delay={index * tokens.motion.stagger} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      height: '100%',
                      p: { xs: 3, md: 4 },
                      borderRadius: `${tokens.radiusCard}px`,
                      bgcolor: tokens.bgPaper,
                      border: `1px solid ${tokens.border}`,
                      boxShadow: tokens.shadowSoft,
                      transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                      '&:hover': {
                        borderColor: '#D9CDBC',
                        boxShadow: tokens.shadowLift,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: `${tokens.radiusControl}px`,
                          bgcolor: isSage ? tokens.sageTint : tokens.copperTint,
                          color: isSage ? tokens.sage : tokens.copper,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={24} strokeWidth={1.5} />
                      </Box>
                      <Chip
                        label={b.tag}
                        size="small"
                        color={isSage ? 'success' : 'secondary'}
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      />
                    </Box>
                    <Typography variant="h3" component="h3" sx={{ mb: 1.5 }}>
                      {b.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.7 }}
                    >
                      {b.body}
                    </Typography>
                    <Box>
                      {b.bullets.map((bullet) => (
                        <Box
                          key={bullet}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.2,
                            py: 1.2,
                            borderTop: `1px solid ${tokens.border}`,
                            '&:last-of-type': {
                              borderBottom: `1px solid ${tokens.border}`,
                            },
                          }}
                        >
                          <Box sx={{ color: tokens.sage, mt: '2px', display: 'flex', flexShrink: 0 }}>
                            <Check size={16} strokeWidth={1.5} />
                          </Box>
                          <Typography
                            sx={{ color: 'text.secondary', fontSize: '0.88rem', lineHeight: 1.55 }}
                          >
                            {bullet}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
